import '../styles/UncontrolledDiagram.scss';
import { useSchema } from 'beautiful-react-diagrams';
import { useEffect, useState } from 'react';
import { Button } from 'beautiful-react-ui';
import { CustomSchema } from './CustomSchema';
import { NodeBuilder } from '../ide/NodeBuilder';
import { BlockType } from '../compiler/factory';
import DiagramIDE from '../ide/DiagramIDE';
import DiagramWrapper from './DiagramWrapper';

interface Props {
    schemaId: string;
}

export default function UncontrolledDiagram(props: Props) {

    let initialSchema = DiagramIDE.getIDE().validatedSchemas.get(props.schemaId);

    const [schema, { onChange, addNode, removeNode }] = useSchema(initialSchema ? initialSchema : new CustomSchema());

    const deleteNodeFromSchema = (id: string) => {
        const nodeToRemove = schema.nodes.find(node => node.id === id);

        if (nodeToRemove) {
            removeNode(nodeToRemove);
        }
    };

    const [nodeBuilder] = useState(new NodeBuilder(props.schemaId));
    const [makeCallback, setMakeCallback] = useState(Function);


    useEffect(() => {
        DiagramIDE.getIDE().addSchema(props.schemaId, schema as CustomSchema, {
            deleteNodeFromSchema: deleteNodeFromSchema,
            onChange: onChange,
            addNode: addNode
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schema]);


    function clickMakeNode(e: React.MouseEvent): void {
        if (makeCallback == null) {
            return;
        }

        makeCallback([e.pageX, e.pageY]);
        setMakeCallback(() => { });
    }

    function scheduleMakeInput(type: BlockType) {
        const callback = (coor: [number, number]) => {
            nodeBuilder.makeInput(type, coor);
        }
        setMakeCallback(() => callback);
    }

    function scheduleMakeOutput(type: BlockType) {
        const callback = (coor: [number, number]) => {
            nodeBuilder.makeOutput(type, coor);
        }
        setMakeCallback(() => callback);
    }

    function scheduleMakeFunction(type: BlockType) {
        const callback = (coor: [number, number]) => {
            nodeBuilder.makeFunction(type, coor);
        }
        setMakeCallback(() => callback);
    }

    function scheduleMakeSpecial(type: BlockType) {
        const callback = (coor: [number, number]) => {
            nodeBuilder.makeSpecial(type, coor);
        }
        setMakeCallback(() => callback);
    }


    return (
        <div className="diagram-section" >
            
            <div className="diagram-display-area" onClick={e => clickMakeNode(e)}>
                {makeCallback !== undefined && makeCallback.name !== "anonymous" && <div className="create-node-prompt">
                    <div className="wrapper">
                        <h2>Click anywhere on canvas to create node</h2>
                    </div>
                </div>}  
                
                <DiagramWrapper schema={schema} onChange={onChange} />
                
            </div>
            <div className={'sidebar'}>
                <Button color="primary" icon="plus" onClick={() => scheduleMakeInput(BlockType.CONST)}>Constant</Button>
                <Button color="primary" icon="plus" onClick={() => scheduleMakeOutput(BlockType.VAR_ASSIGN)}>Variable Assign</Button>
                <Button color="primary" icon="plus" onClick={() => scheduleMakeInput(BlockType.VAR_RETRIEVE)}>Variable Get</Button>
                <Button color="primary" icon="plus" onClick={() => scheduleMakeOutput(BlockType.VAR_DELETE)}>Variable Delete</Button>
                <hr />
                <Button color="primary" icon="plus" onClick={() => scheduleMakeOutput(BlockType.ARR_CREATE)}>Create Array</Button>
                <Button color="primary" icon="plus" onClick={() => scheduleMakeOutput(BlockType.ARR_SET_IX)}>Array Set Index</Button>
                <Button color="primary" icon="plus" onClick={() => scheduleMakeInput(BlockType.ARR_GET_IX)}>Array Get Index</Button>
                <hr />
                <Button color="primary" icon="plus" onClick={() => scheduleMakeFunction(BlockType.SUM)}>Adder</Button>
                <Button color="primary" icon="plus" onClick={() => scheduleMakeFunction(BlockType.SUBTRACT)}>Subtract</Button>
                <Button color="primary" icon="plus" onClick={() => scheduleMakeFunction(BlockType.PROD)}>Multiply</Button>
                <Button color="primary" icon="plus" onClick={() => scheduleMakeFunction(BlockType.DIV)}>Divide</Button>
                <hr />
                <Button color="primary" icon="plus" onClick={() => scheduleMakeOutput(BlockType.MAKE_CONTROL_ELEM)}>Create Control</Button>
                <Button color="primary" icon="plus" onClick={() => scheduleMakeInput(BlockType.JOY_X)}>Joystick X</Button>
                <Button color="primary" icon="plus" onClick={() => scheduleMakeInput(BlockType.JOY_Y)}>Joystick Y</Button>
                <hr />
                <Button color="primary" icon="plus" onClick={() => scheduleMakeOutput(BlockType.MAKE_SHAPE)}>Shape</Button>
                <Button color="primary" icon="arrow-up" onClick={() => scheduleMakeInput(BlockType.GET_SHAPE)}>Get Shape</Button>
                <Button color="primary" icon="plus" onClick={() => scheduleMakeOutput(BlockType.MOVE_SHAPE)}>Move Shape</Button>
                <Button color="primary" icon="plus" onClick={() => scheduleMakeOutput(BlockType.ROTATE_SHAPE)}>Rotate Shape</Button>
                <Button color="primary" icon="plus" onClick={() => scheduleMakeOutput(BlockType.SCALE_SHAPE)}>Stretch Shape</Button>
                <Button color="primary" icon="plus" onClick={() => scheduleMakeFunction(BlockType.GET_X)}>Get X</Button>
                <Button color="primary" icon="plus" onClick={() => scheduleMakeFunction(BlockType.GET_Y)}>Get Y</Button>

                <hr />
                <Button color="primary" icon="plus" onClick={() => scheduleMakeFunction(BlockType.GEQ)}>&gt;=</Button>
                <Button color="primary" icon="plus" onClick={() => scheduleMakeFunction(BlockType.LEQ)}>&lt;=</Button>
                <Button color="primary" icon="plus" onClick={() => scheduleMakeFunction(BlockType.LT)}>&lt;</Button>
                <Button color="primary" icon="plus" onClick={() => scheduleMakeFunction(BlockType.GT)}>&gt;</Button>
                <Button color="primary" icon="plus" onClick={() => scheduleMakeFunction(BlockType.EQ)}>==</Button>
                <Button color="primary" icon="plus" onClick={() => scheduleMakeFunction(BlockType.AND)}>AND</Button>
                <Button color="primary" icon="plus" onClick={() => scheduleMakeFunction(BlockType.OR)}>OR</Button>
                <Button color="primary" icon="plus" onClick={() => scheduleMakeFunction(BlockType.NOT)}>NOT</Button>
                <hr />
                <Button color="primary" icon="plus" onClick={() => scheduleMakeFunction(BlockType.TERNARY)}>if then else</Button>
                <Button color="primary" icon="plus" onClick={() => scheduleMakeSpecial(BlockType.IF)}>IF</Button>
                <Button color="primary" icon="plus" onClick={() => scheduleMakeSpecial(BlockType.WHILE)}>WHILE</Button>


                <hr />
                <Button color="primary" icon="plus" onClick={() => scheduleMakeOutput(BlockType.CLB)}>Print</Button>
            </div>
        </div>
    );
};